import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Flex } from '../../../components/ui/Flex';
import { HeaderText, ModalBodyContent, ModalButtonsContainer, ModalHeader, SectionContainer, SectionSubtitle, StyledModalBody, StyledModalV2 } from '../../../components/Modals';
import { ButtonBodyVariant } from '../../../components/ui/ButtonVariant';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/Form';
import { useForm } from 'react-hook-form';
import { SettingsModalInput } from '../../settings/SettingsModals';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { closeActiveModal } from '../../../helper/modals';
import { searchIntercomUsers, updateIntercomUser } from '../../../redux/actions/intercom';
import { setToast } from '../../../redux/actions/window';
import { makePossessive } from '../../conversation/templates/helper';
import i18next from 'i18next';
import { requestPending } from '../../../utils/status';
import { UPDATE_INTERCOM_USER } from '../../../redux/constants';
import { useGetConfigs } from '../../../hooks/useGetConfigs';

const schema = Yup.object().shape({
  first_name: Yup.string()
    .max(64, i18next.t('Not more than 64 characters'))
    .required(i18next.t('First name is required!')),
  last_name: Yup.string()
    .max(64, i18next.t('Not more than 64 characters'))
    .optional(),
  phone_number: Yup.string()
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(10, i18next.t('Phone number should be 10 digits long'))
    .max(10, i18next.t('Phone number should be 10 digits long'))
    .required(i18next.t('Phone number is required!')),
  private_pin: Yup.string()
    .optional()
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(4, i18next.t('Private pin should be 4 digits long'))
    .max(4, i18next.t('Private pin should be 4 digits long'))
});

const EditUserIntercomPinModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { modal, data: { intercomUser, query } } = useSelector((state) => state.headerModal);
  const globalSelectedBuilding = useSelector((state) => state.building.globalSelectedBuilding);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);
  const { config } = useGetConfigs();

  const updateIntercomUserStatus = useSelector((state) => state.intercom.updateIntercomUserStatus);

  const form = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: intercomUser.original_first_name,
      last_name: intercomUser.original_last_name,
      phone_number: intercomUser.phone_number,
      private_pin: intercomUser.private_pin
    }
  });

  const handleSubmit = (values) => {
    const body = {
      first_name: values.first_name,
      last_name: values.last_name,
    }

    if (form.formState.dirtyFields.phone_number) {
      body.phone_number = values.phone_number;
    }

    if (config?.enable_akuvox_private_pins && form.formState.dirtyFields.private_pin) {
      body.private_pin = values.private_pin;
    }

    dispatch(
      updateIntercomUser({
        orgId: globalOrgId,
        buildingId: globalSelectedBuilding?.id,
        userId: intercomUser.user_id,
        body: {
          ...body,
        },
        success: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Updated user information')
            })
          );
          dispatch(searchIntercomUsers({
            orgId: globalOrgId,
            buildingId: globalSelectedBuilding.id,
            queryString: query,
          }))
          closeActiveModal()
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Could not update user information'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const randomizePin = () => {
    form.setValue('private_pin', Array.from({ length: 4 }, () => Math.floor(Math.random() * 10)).join(''), { shouldDirty: true });
  }

  const resetPin = () => {
    form.resetField('private_pin');
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <StyledModalV2
          show={modal === 'EditUserIntercomPinModal'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'550px'}
          side
        >
          <StyledModalBody>
            <ModalHeader v2 title={`${t('Edit intercom user information')}`} />
            <ModalBodyContent>
              <SectionContainer>
                <HeaderText alt>{t('Intercom information')}</HeaderText>
                <SectionSubtitle>
                  Update {makePossessive(`${form.getValues('first_name')} ${form.getValues('last_name')}`)} directory information.
                  This will overwrite the users name on the physical intercom. Any existing names or formatting will be replaced.
                </SectionSubtitle>
                <Flex direction="column" gap="16px">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem alt>
                        <FormLabel v3>{t('First Name')}</FormLabel>
                        <FormControl>
                          <SettingsModalInput
                            type="text"
                            placeholder={t('Enter first name')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem alt>
                        <FormLabel v3>{t('Last Name')}</FormLabel>
                        <FormControl>
                          <SettingsModalInput
                            type="text"
                            placeholder={t('Enter last name')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem alt>
                        <FormLabel v3>{t('Phone Number')}</FormLabel>
                        <FormControl>
                          <SettingsModalInput
                            type="text"
                            placeholder={t('Enter phone number')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {config?.enable_akuvox_private_pins &&
                    <FormField
                      control={form.control}
                      name="private_pin"
                      render={({ field }) => (
                        <Flex direction="column" gap="16px" alignItems="end">
                          <FormItem alt>
                            <FormLabel v3>{t('Private pin')}</FormLabel>
                            <FormControl>
                              <SettingsModalInput
                                type="text"
                                placeholder={t('Enter private pin')}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                          <Flex direction="row" gap="16px">
                            <ButtonBodyVariant onClick={resetPin} disabled={!form.formState.dirtyFields.private_pin} cssOverride={"width: fit-content;"} type='button'>
                              Reset
                            </ButtonBodyVariant>

                            <ButtonBodyVariant onClick={randomizePin} cssOverride={"width: fit-content;"} type='button'>
                              Randomize PIN
                            </ButtonBodyVariant>
                          </Flex>
                        </Flex>
                      )}
                    />
                  }

                </Flex>
              </SectionContainer>
            </ModalBodyContent>
          </StyledModalBody>

          <ModalButtonsContainer gapY="8px" isAtBottom>
            <ButtonBodyVariant type="submit" cssOverride={"width: 100%;"} disabled={!form.formState.isDirty || form.formState.isSubmitting || updateIntercomUserStatus === requestPending(UPDATE_INTERCOM_USER)}>{t('Save changes')}</ButtonBodyVariant>
          </ModalButtonsContainer>
        </StyledModalV2>
      </form>
    </Form>
  );
};

export default EditUserIntercomPinModal;
